// routeLegService.js - Pre-computes walking route legs between guide stops via ORS

const ORS_API_KEY =
  'eyJvcmciOiI1YjNjZTM1OTc4NTExMTAwMDFjZjYyNDgiLCJpZCI6IjI4MmViNjUyNjQ5NjRlZDI5MjA2MmM3N2I2NDJkZDg0IiwiaCI6Im11cm11cjY0In0=';
export const ROUTE_MODE_OPTIONS = [
  { value: 'walking', label: 'Walking', orsProfile: 'foot-walking' },
  { value: 'driving', label: 'Driving', orsProfile: 'driving-car' },
  { value: 'cycling', label: 'Cycling', orsProfile: 'cycling-regular' },
];

const ROUTE_MODE_CONFIG = ROUTE_MODE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option;
  return acc;
}, {});
export const DEFAULT_ROUTE_MODE = 'walking';

// Delay between ORS calls to respect the 40 req/min free tier limit (1.5 s)
const RATE_LIMIT_DELAY_MS = 1500;

// Coordinate precision: 5 decimal places ≈ 1 m accuracy — enough for walking navigation
const COORD_PRECISION = 5;

// RDP simplification tolerance in degrees (~2–3 m at mid-latitudes). Higher = fewer points.
const RDP_TOLERANCE = 0.00003;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const round = (n) => parseFloat(n.toFixed(COORD_PRECISION));

/**
 * Ramer-Douglas-Peucker polyline simplification.
 * Points are [lat, lng]. Returns a reduced subset that preserves the shape.
 */
function rdpSimplify(points, tolerance) {
  if (points.length <= 2) return points;

  // Find the point farthest from the line between first and last
  const [ax, ay] = points[0];
  const [bx, by] = points[points.length - 1];
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;

  let maxDist = 0;
  let maxIdx = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i];
    let dist;
    if (len2 === 0) {
      dist = Math.sqrt((px - ax) ** 2 + (py - ay) ** 2);
    } else {
      const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
      dist = Math.sqrt((px - (ax + t * dx)) ** 2 + (py - (ay + t * dy)) ** 2);
    }
    if (dist > maxDist) { maxDist = dist; maxIdx = i; }
  }

  if (maxDist > tolerance) {
    const left = rdpSimplify(points.slice(0, maxIdx + 1), tolerance);
    const right = rdpSimplify(points.slice(maxIdx), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  return [points[0], points[points.length - 1]];
}

/**
 * Call ORS for a single leg between two stops.
 * Returns a leg object or null if the call fails.
 */
async function fetchLeg(fromStop, toStop, orsProfile, routeMode) {
  const fromLng = parseFloat(fromStop.coordinates.longitude);
  const fromLat = parseFloat(fromStop.coordinates.latitude);
  const toLng = parseFloat(toStop.coordinates.longitude);
  const toLat = parseFloat(toStop.coordinates.latitude);

  const response = await fetch(`https://api.openrouteservice.org/v2/directions/${orsProfile}/geojson`, {
    method: 'POST',
    headers: {
      Authorization: ORS_API_KEY,
      'Content-Type': 'application/json',
      Accept: 'application/geo+json',
    },
    body: JSON.stringify({
      coordinates: [
        [fromLng, fromLat],
        [toLng, toLat],
      ],
    }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`ORS ${response.status}: ${body.slice(0, 200)}`);
  }

  const geojson = await response.json();
  return parseOrsResponse(geojson, fromStop.id, toStop.id, routeMode);
}

/**
 * Parse the ORS GeoJSON response into the leg schema expected by the app.
 * GeoJSON coordinates are [lng, lat]; we store them as [lat, lng].
 */
function parseOrsResponse(geojson, fromStopId, toStopId, routeMode) {
  const feature = geojson.features[0];
  const { summary, segments } = feature.properties;
  const orsCoords = feature.geometry.coordinates; // [lng, lat]

  // Reverse to [lat, lng], round, then simplify
  const rawPolyline = orsCoords.map(([lng, lat]) => [round(lat), round(lng)]);
  const polyline = rdpSimplify(rawPolyline, RDP_TOLERANCE);

  const steps = (segments[0]?.steps || []).map((step) => {
    const wpIndex = step.way_points[0];
    const [lng, lat] = orsCoords[wpIndex];
    return {
      instruction: step.instruction,           // English fallback (ORS default)
      street_name: step.name || '',            // Street/path name for i18n reconstruction
      distance_meters: Math.round(step.distance),
      maneuver_type: step.type,               // 0–13, see ORS maneuver type table
      waypoint: [round(lat), round(lng)],
    };
  });

  return {
    from_stop_id: fromStopId,
    to_stop_id: toStopId,
    route_mode: routeMode,
    distance_meters: Math.round(summary.distance),
    duration_seconds: Math.round(summary.duration),
    polyline,
    steps,
  };
}

/**
 * Determine whether a stop should be treated as indoor.
 * The spec uses an explicit `is_indoor` flag on each point.
 */
function isIndoorStop(stop) {
  return stop.is_indoor === true;
}

function getPointType(stop) {
  const pointType = String(stop?.pointType || '').trim();
  if (pointType) return pointType === 'bus_stop' ? 'popup_stop' : pointType;
  return stop?.category === 'bus_stop' || stop?.category === 'popup_stop'
    ? 'popup_stop'
    : 'stop';
}

function isNarratedStop(stop) {
  return getPointType(stop) !== 'popup_stop';
}

/**
 * Compute all walking legs for a guide and return the enriched itinerary.
 *
 * @param {object} itinerary  - Full guide JSON object (metadata + points)
 * @param {function} onProgress - Optional callback(message: string) for progress updates
 * @returns {object} - Itinerary with a `legs` array added (or updated)
 */
export async function computeSingleRouteLeg(fromStop, toStop, routeMode = DEFAULT_ROUTE_MODE) {
  const selectedMode = ROUTE_MODE_CONFIG[routeMode] ? routeMode : DEFAULT_ROUTE_MODE;
  const { orsProfile } = ROUTE_MODE_CONFIG[selectedMode];
  const leg = await fetchLeg(fromStop, toStop, orsProfile, selectedMode);
  leg.route_mode = selectedMode;
  leg.ors_profile = orsProfile;
  leg.route_source = 'auto';
  return leg;
}

export async function computeRoutelegs(itinerary, onProgress = () => {}, routeMode = DEFAULT_ROUTE_MODE) {
  const selectedMode = ROUTE_MODE_CONFIG[routeMode] ? routeMode : DEFAULT_ROUTE_MODE;
  const { orsProfile, label } = ROUTE_MODE_CONFIG[selectedMode];
  const guideType = itinerary.metadata?.guide_type || 'outdoor';

  // Indoor guides never get legs
  if (guideType === 'indoor') {
    onProgress('Guide type is "indoor" — skipping leg computation.');
    return { ...itinerary, legs: [] };
  }

  const points = (itinerary.points || []).filter(isNarratedStop);
  if (points.length < 2) {
    onProgress('Guide has fewer than 2 stops — no legs to compute.');
    return { ...itinerary, legs: [] };
  }

  onProgress(`Using ${label.toLowerCase()} routing profile.`);

  const legs = [];
  let skipped = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];

    // Hybrid guides: skip pairs where either stop is indoor
    if (guideType === 'hybrid' && (isIndoorStop(from) || isIndoorStop(to))) {
      onProgress(
        `Skipping leg ${i + 1}/${points.length - 1}: one or both stops are indoor.`
      );
      skipped++;
      continue;
    }

    // Validate coordinates
    const hasCoords = (stop) =>
      stop.coordinates &&
      stop.coordinates.latitude != null &&
      stop.coordinates.longitude != null &&
      stop.coordinates.latitude !== '' &&
      stop.coordinates.longitude !== '';

    if (!hasCoords(from) || !hasCoords(to)) {
      onProgress(
        `Skipping leg ${i + 1}/${points.length - 1}: missing coordinates on "${from.name || from.id}" or "${to.name || to.id}".`
      );
      skipped++;
      continue;
    }

    onProgress(
      `Computing leg ${i + 1}/${points.length - 1}: "${from.name || from.id}" → "${to.name || to.id}"…`
    );

    try {
      const leg = await fetchLeg(from, to, orsProfile, selectedMode);
      leg.route_mode = selectedMode;
      leg.ors_profile = orsProfile;
      leg.route_source = 'auto';
      legs.push(leg);
      onProgress(
        `  ✓ ${leg.distance_meters} m, ${Math.round(leg.duration_seconds / 60)} min (${leg.polyline.length} pts)`
      );
    } catch (err) {
      onProgress(`  ✗ Error: ${err.message} — leg skipped.`);
      console.error(`Route leg error (stop ${i} → ${i + 1}):`, err);
    }

    // Rate-limit: wait before the next call (skip after the last iteration)
    if (i < points.length - 2) {
      await sleep(RATE_LIMIT_DELAY_MS);
    }
  }

  onProgress(
    `Done. ${legs.length} leg(s) computed, ${skipped} skipped.`
  );

  return { ...itinerary, legs, route_mode: selectedMode };
}




